import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useAlbums } from '../../hooks/useAlbums'
import {
  LayoutGrid,
  Image,
  Upload,
  FolderHeart,
  Settings,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  Plus,
  Menu,
  X,
  Users,
  AlertTriangle,
} from 'lucide-react'
import { useState, useEffect } from 'react'

interface LayoutProps {
  children: React.ReactNode
  title?: string
  actions?: React.ReactNode
}

export const Layout = ({ children, title, actions }: LayoutProps) => {
  const { user, logout, isAuthenticated } = useAuthStore()
  // The album list only feeds the authenticated sidebar; skip the fetch for
  // logged-out visitors (share pages, etc.) to avoid pointless API calls.
  const { data: albums } = useAlbums(undefined, undefined, isAuthenticated)
  const location = useLocation()
  const navigate = useNavigate()
  const [showAlbums, setShowAlbums] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen])

  return (
    <div className="min-h-screen bg-white flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Only show when authenticated */}
      {isAuthenticated && (
        <aside className={`
          w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full z-50
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Logo & Close Button */}
          <div className="p-4 lg:p-6 border-b border-gray-100 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 rounded-xl flex items-center justify-center">
                <Image className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="text-lg lg:text-xl font-bold text-gray-900">
                Gallery
              </span>
            </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation menu"
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            <NavItem
              to="/"
              icon={<LayoutGrid className="w-5 h-5" />}
              label="All Photos"
              active={isActive('/') && !location.pathname.startsWith('/album/')}
              onClick={() => setSidebarOpen(false)}
            />
            
            <NavItem
              to="/admin/upload"
              icon={<Upload className="w-5 h-5" />}
              label="Upload"
              active={isActive('/admin/upload')}
              onClick={() => setSidebarOpen(false)}
            />
          </div>

          {/* Albums Section */}
          <div className="mt-6">
            <button
              onClick={() => setShowAlbums(!showAlbums)}
              aria-expanded={showAlbums}
              aria-controls="albums-list"
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
            >
              <span className="flex items-center gap-2">
                <FolderHeart className="w-4 h-4" />
                Albums
              </span>
              {showAlbums ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {showAlbums && (
              <div className="mt-2 space-y-1">
                {albums?.map((album) => (
                  <NavItem
                    key={album.albumId}
                    to={`/album/${album.albumId}`}
                    label={album.name}
                    active={location.pathname === `/album/${album.albumId}`}
                    count={album.imageCount}
                    onClick={() => setSidebarOpen(false)}
                  />
                ))}
                
                {isAuthenticated && (
                  <Link
                    to="/admin/albums"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Manage Albums</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Admin Section */}
          {isAuthenticated && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="px-3 text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                Admin
              </p>
              <NavItem
                to="/admin"
                icon={<Settings className="w-5 h-5" />}
                label="Dashboard"
                active={isActive('/admin') && location.pathname === '/admin'}
                onClick={() => setSidebarOpen(false)}
              />
              <NavItem
                to="/admin/users"
                icon={<Users className="w-5 h-5" />}
                label="Users"
                active={isActive('/admin/users')}
                onClick={() => setSidebarOpen(false)}
              />
              <NavItem
                to="/admin/orphans"
                icon={<AlertTriangle className="w-5 h-5" />}
                label="Orphaned Photos"
                active={isActive('/admin/orphans')}
                onClick={() => setSidebarOpen(false)}
              />

            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-200">
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 lg:w-9 lg:h-9 bg-gray-700 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.googleEmail || user?.email || 'Admin'}
                </p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <User className="w-4 h-4" />
              Sign In
            </Link>
          )}
        </div>
      </aside>
      )}

      {/* Minimal Header for Logged Out Users */}
      {!isAuthenticated && (
        <header className="fixed top-0 left-0 right-0 h-[60px] bg-white border-b border-gray-200 z-50 flex items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-3">
            <Image className="w-6 h-6 text-gray-700" />
            <span className="text-lg font-semibold text-gray-900">Gallery</span>
          </Link>
          <Link
            to="/login"
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            title="Sign In"
          >
            <User className="w-5 h-5" />
          </Link>
        </header>
      )}

      {/* Main Content */}
      <main className={`flex-1 lg:ml-0 ${!isAuthenticated ? 'pt-[60px]' : ''}`}>
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <span className="font-semibold text-gray-900">
              {title || 'Gallery'}
            </span>
            
              {actions ? (
                <div className="flex items-center gap-1">{actions}</div>
              ) : (
              <div className="w-10">
                {/* Spacer for centering */}
              </div>
            )}
          </div>
        </header>

        {/* Desktop Header */}
        {(title || actions) && (
          <header className="hidden lg:block bg-white border-b border-gray-200 sticky top-0 z-20">
            <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
              {title && <h1 className="text-2xl font-bold text-gray-900">{title}</h1>}
              {actions && <div className="flex items-center gap-3">{actions}</div>}
            </div>
          </header>
        )}
        
        {/* Page Content */}
        <div className="p-4 lg:p-6">{children}</div>
      </main>
    </div>
  )
}

interface NavItemProps {
  to: string
  icon?: React.ReactNode
  label: string
  active?: boolean
  count?: number
  onClick?: () => void
}

const NavItem = ({ to, icon, label, active, count, onClick }: NavItemProps) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-blue-50 text-blue-700'
        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    <span className="flex items-center gap-3">
      {icon && <span className={active ? 'text-blue-600' : 'text-gray-400'}>{icon}</span>}
      {!icon && <span className="w-5" />}
      <span className="truncate">{label}</span>
    </span>
    {count !== undefined && count > 0 && (
      <span className="text-xs text-gray-400">{count}</span>
    )}
  </Link>
)